import { type CalendarDate, getDayOfWeek, isSameDay, isSameMonth } from "@internationalized/date";
import { useCalendarCell } from "@react-aria/calendar";
import { useFocusRing } from "@react-aria/focus";
import { useLocale } from "@react-aria/i18n";
import { mergeProps } from "@react-aria/utils";
import { type CalendarState, type RangeCalendarState } from "@react-stately/calendar";
import cx from "classnames";
import { type FC, useRef } from "react";
import { type DateValue } from "react-aria";

export const CalendarCell: FC<{
	date: CalendarDate;
	state: CalendarState | RangeCalendarState;
	currentMonth: DateValue;
}> = ({ state, date, currentMonth }) => {
	const ref = useRef<HTMLDivElement>(null);
	const { cellProps, buttonProps, isSelected, isDisabled, formattedDate } = useCalendarCell(
		{ date },
		state,
		ref
	);

	const isOutsideMonth = !isSameMonth(currentMonth, date);

	// The start and end date of the selected range will have
	// an emphasized appearance.
	const isSelectionStart =
		"highlightedRange" in state && state.highlightedRange
			? isSameDay(date, state.highlightedRange.start)
			: isSelected;
	const isSelectionEnd =
		"highlightedRange" in state && state.highlightedRange
			? isSameDay(date, state.highlightedRange.end)
			: isSelected;

	// We add rounded corners on the left for the first day of the month,
	// the first day of each week, and the start date of the selection.
	// We add rounded corners on the right for the last day of the month,
	// the last day of each week, and the end date of the selection.
	const { locale } = useLocale();
	const dayOfWeek = getDayOfWeek(date, locale);
	const isRoundedLeft = isSelected && (isSelectionStart || dayOfWeek === 0 || date.day === 1);
	const isRoundedRight =
		isSelected &&
		(isSelectionEnd || dayOfWeek === 6 || date.day === date.calendar.getDaysInMonth(date));

	const { focusProps, isFocusVisible } = useFocusRing();

	return (
		<td {...cellProps} className={`relative py-0.5 ${isFocusVisible ? "z-10" : "z-0"}`}>
			<div
				{...mergeProps(buttonProps, focusProps)}
				ref={ref}
				hidden={isOutsideMonth}
				className={cx(`group h-8 w-8 outline-none`, {
					"rounded-l-full": isRoundedLeft,
					"rounded-r-full": isRoundedRight,
					"bg-tertiary": isSelected,
					"disabled": isDisabled,
				})}
			>
				<div
					className={cx(
						`flex h-full w-full cursor-default items-center justify-center rounded-full`,
						{
							"text-tertiary": isDisabled,

							// Focus ring, visible while the cell has keyboard focus.
							"ring-4 ring-default": isFocusVisible,

							// Darker selection background for the start and end.
							"bg-brand text-fixed-light-default hover:bg-brand-hover":
								isSelectionStart || isSelectionEnd,

							// Hover state for cells in the middle of the range.
							"hover:bg-quaternary":
								isSelected && !(isSelectionStart || isSelectionEnd),

							// Hover state for non-selected cells.
							"hover:bg-button-normal-secondary-hover": !isSelected && !isDisabled,
						}
					)}
				>
					{formattedDate}
				</div>
			</div>
		</td>
	);
};
